import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {HeadingLarge, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";

import type {StandardRegionPresentationsQuery_standardRegionPresentations_presentationSections as PresentationSections} from "./__graphql-types__/StandardRegionPresentationsQuery.ts";

type Props = {
    presentationSection: PresentationSections | null | undefined;
};

export const SetOfStandardsPresentationAlignment = ({
    presentationSection,
}: Props): React.ReactElement | null => {
    if (!presentationSection) {
        return null;
    }

    const alignedCourses = !presentationSection.courses?.length ? null : (
        <View>
            <LabelLarge tag="h3" style={styles.listHeading}>
                {doNotTranslate`Courses`}
            </LabelLarge>
            <ul
                className={css(
                    presentationSection.courses.length > 4
                        ? styles.largeCoursesList
                        : styles.smallCoursesList,
                )}
            >
                {presentationSection.courses.map((course) => {
                    if (
                        !course ||
                        !course.relativeUrl ||
                        !course.standaloneTitle
                    ) {
                        return null;
                    }
                    return (
                        <li className={css(styles.listItem)} key={course.id}>
                            <Link
                                style={styles.listLink}
                                href={course.relativeUrl}
                            >
                                {course.standaloneTitle}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </View>
    );

    const alignmentGuides = !presentationSection.rootStandards.length ? null : (
        <View>
            <LabelLarge tag="h3" style={styles.listHeading}>
                {doNotTranslate`Browse by standards`}
            </LabelLarge>
            <ul
                className={css(
                    presentationSection.rootStandards.length > 4
                        ? styles.largeStandardsList
                        : styles.smallStandardsList,
                )}
            >
                {presentationSection.rootStandards.map((standard) => (
                    <li
                        className={css(styles.listItem)}
                        key={standard.standardId}
                    >
                        <Link
                            style={styles.listLink}
                            href={standard.relativeUrl}
                        >
                            {standard.displayName}
                        </Link>
                    </li>
                ))}
            </ul>
        </View>
    );

    return (
        <View key={presentationSection.setOfStandardsId}>
            <HeadingLarge style={styles.setHeading}>
                {presentationSection.displayName}
            </HeadingLarge>
            {alignedCourses}
            {alignmentGuides}
        </View>
    );
};

const styles = StyleSheet.create({
    largeCoursesList: {
        columnCount: 1,
        [mediaQueries.mdOrLarger]: {
            columnCount: 2,
            maxWidth: 700,
        },
    },
    smallCoursesList: {
        columnCount: 1,
        [mediaQueries.mdOrLarger]: {
            columnCount: 1,
        },
    },
    listHeading: {
        fontSize: 20,
        lineHeight: "32px",
        marginTop: 4,
        marginBottom: 18, // 32px from previous baseline to next ascent line
    },
    listItem: {
        // Using padding instead of margin here for consistent spacing when
        // there's an uneven number of items in the columns.
        paddingBottom: 13, // 24px from previous baseline to next ascent line.
    },
    listLink: {
        color: color.offBlack,
        fontSize: 18,
        lineHeight: "24px",
        ":focus-visible": {
            color: color.offBlack,
            outline: `1px solid ${color.offBlack}`,
        },
    },
    setHeading: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 36,
        lineHeight: "32px",
        marginBottom: 13, // 32px from previous baseline to next ascent line.
        [mediaQueries.smOrSmaller]: {
            fontSize: 24, // Override more specific selector from @khanacademy/wonder-blocks-typography
            lineHeight: "32px", // Override more specific selector from @khanacademy/wonder-blocks-typography
            marginBottom: 9, // 32px from previous baseline to next ascent line.
        },
        [mediaQueries.mdOrLarger]: {
            fontSize: 36, // Override more specific selector from @khanacademy/wonder-blocks-typography
            lineHeight: "32px", // Override more specific selector from @khanacademy/wonder-blocks-typography
        },
    },
    largeStandardsList: {
        columnCount: 1,
        [mediaQueries.mdOrLarger]: {
            columnCount: 3,
        },
        marginBottom: 32,
    },
    smallStandardsList: {
        columnCount: 1,
        [mediaQueries.mdOrLarger]: {
            columnCount: 1,
        },
        marginBottom: 32,
    },
});
